import React from "react";
import * as Sentry from "@sentry/react";
import ErrorPage from "./ErrorPage.js";
import NotFoundPage from "./NotFoundPage.js";
import PermissionDeniedPage from "./PermissionDeniedPage.js";

/**
 * useRoutes (unlike createBrowserRouter) has no errorElement, so this catches
 * what the router's own per-route error boundary used to. Mounted inside
 * BrowserRouter, so the pages it renders can use useNavigate.
 *
 * A page-driving query (one that doesn't opt out with its own throwOnError:
 * false — see client/src/index.js) throws here on any 4xx/5xx response, so a
 * backend permission/not-found check surfaces as the matching page with no
 * per-page handling required: 403 -> PermissionDeniedPage, 404 -> NotFoundPage
 * (the same component client-side param validation already renders directly,
 * e.g. ContentDetailRoute's invalid-id check — this is just a second way to
 * reach it), anything else -> the generic ErrorPage.
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, status: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, status: error?.response?.status ?? null };
  }

  componentDidCatch(error, info) {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.status === 403) return <PermissionDeniedPage />;
    if (this.state.status === 404) return <NotFoundPage />;
    return <ErrorPage />;
  }
}
